/**
 * React hook for audio management
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { createAudioManager, AudioManager } from '../utils/audio';
import type { UseAudioOptions, UseAudioReturn } from '../types/wheel';

// Default audio paths - can be overridden
const DEFAULT_TICK_PATH = '/media/spinner-wheel-tick.mp3';
const DEFAULT_CELEBRATION_PATH = '/media/spinner-wheel-celebration.mp3';

export function useAudio(options: UseAudioOptions = {}): UseAudioReturn {
  const {
    enabled = true,
    tickPath = DEFAULT_TICK_PATH,
    celebrationPath = DEFAULT_CELEBRATION_PATH,
    tickVolume = 0.5,
    celebrationVolume = 0.7,
  } = options;

  const [isEnabled, setIsEnabled] = useState(enabled);
  const audioManagerRef = useRef<AudioManager | null>(null);

  // Initialize audio manager
  useEffect(() => {
    if (typeof window === 'undefined') return;

    audioManagerRef.current = createAudioManager(tickPath, celebrationPath);
    audioManagerRef.current.setVolume(tickVolume, celebrationVolume);
    // Warm up: start fetching + decoding the tick/celebration buffers now so
    // the first spin doesn't drop its early ticks waiting on the network.
    // decodeAudioData works on a suspended AudioContext; resume() still waits
    // for the user gesture inside playTick/playCelebration.
    audioManagerRef.current.prepare();

    return () => {
      audioManagerRef.current?.dispose();
      audioManagerRef.current = null;
    };
  }, [tickVolume, celebrationVolume]);

  // Update enabled state when prop changes
  useEffect(() => {
    setIsEnabled(enabled);
  }, [enabled]);

  const playTick = useCallback(() => {
    if (isEnabled && audioManagerRef.current) {
      audioManagerRef.current.playTick();
    }
  }, [isEnabled]);

  const prepare = useCallback(() => {
    if (isEnabled && audioManagerRef.current) {
      audioManagerRef.current.prepare();
    }
  }, [isEnabled]);

  const playCelebration = useCallback(() => {
    if (isEnabled && audioManagerRef.current) {
      audioManagerRef.current.playCelebration();
    }
  }, [isEnabled]);

  const stopAll = useCallback(() => {
    audioManagerRef.current?.stopAll();
  }, []);

  const setEnabled = useCallback((value: boolean) => {
    setIsEnabled(value);
  }, []);

  return {
    prepare,
    playTick,
    playCelebration,
    stopAll,
    setEnabled,
  };
}
